import * as React from "react"
import { Lock } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { useTranslations } from "@/hooks/use-translations"
import { whatsappService, type WhatsAppNumber } from "@/services/whatsappService"

interface WhatsAppLineFilterProps {
  /** `phone_number_id` activo, o null para todos. */
  value: string | null
  onChange: (phoneNumberId: string | null) => void
}

/**
 * Los numeros de WhatsApp del cliente, como filtro de la bandeja.
 *
 * Los numeros salen de la API, nunca escritos a mano: quien conecte uno nuevo
 * lo ve aqui sin tocar codigo. Con menos de dos numeros conectados no hay nada
 * que elegir y el componente no pinta nada.
 */
export function WhatsAppLineFilter({ value, onChange }: WhatsAppLineFilterProps) {
  const { t } = useTranslations()
  const [numeros, setNumeros] = React.useState<WhatsAppNumber[]>([])

  React.useEffect(() => {
    let cancelado = false

    whatsappService
      .getConnections()
      .then((conexiones) => {
        if (cancelado) return
        setNumeros(conexiones.filter((conexion) => conexion.connected && conexion.phone_number_id))
      })
      .catch(() => {
        // Sin numeros no hay filtro que ofrecer; la bandeja sigue funcionando.
        if (!cancelado) setNumeros([])
      })

    return () => {
      cancelado = true
    }
  }, [])

  if (numeros.length < 2) return null

  const etiquetaDe = (numero: WhatsAppNumber) =>
    numero.display_name?.trim() || numero.display_phone_number?.trim() || numero.phone_number_id

  return (
    <TooltipProvider>
      <div className="flex items-center gap-1 overflow-x-auto px-4 pb-2" data-testid="whatsapp-line-filter">
        <Button
          variant="ghost"
          size="xs"
          className={cn("shrink-0", value === null && "bg-muted")}
          onClick={() => onChange(null)}
        >
          {t("conversations.line_all") || "Todos los números"}
        </Button>

        {numeros.map((numero) => (
          <Tooltip key={numero.phone_number_id}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="xs"
                className={cn(
                  "shrink-0 gap-1",
                  value === numero.phone_number_id && "bg-muted",
                )}
                onClick={() => onChange(numero.phone_number_id)}
              >
                {numero.is_internal && <Lock className="h-3 w-3" />}
                <span className="whitespace-nowrap">{etiquetaDe(numero)}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{numero.display_phone_number || numero.phone_number_id}</p>
              {numero.is_internal && (
                <p className="text-xs text-muted-foreground">
                  {t("conversations.line_internal") || "Interno"}
                </p>
              )}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  )
}
